import React, { useEffect, useMemo, useState } from 'react';
import { Input, Autocomplete, AutocompleteItem, Button, Chip, Checkbox } from '@heroui/react';
import { Award, X } from 'lucide-react';
import { AppDatePicker } from '../../resources/components/AppDatePicker.js';
import { fieldsForType, labelFor } from '../certificateFields.js';
import { gradesForType, ratingsForType } from '../certificateCatalogs.js';
import { toPickerValue, expiryState } from '../documentDates.js';
import { formatUserDate } from '../formatUserDate.js';
import { toAttachmentList } from '../attachments.js';
import AttachmentList from './AttachmentList.js';
import CredentialDrawer, { PreviewRow } from './CredentialDrawer.js';

/**
 * CertificateDialog — add / edit one certificate.
 *
 * The certificate type rewrites the whole form: which controls exist comes
 * from `fieldsForType`, and what they are CALLED comes from `labelFor` — the
 * same input reads "Grade" on a pilot certificate and "Class" on a medical,
 * "Date Of Issue" against "Date of Examination". Both maps are the legacy
 * ones, lifted unchanged, so the form asks for exactly what it always asked.
 *
 * Ratings are the type-scoped catalog minus that type's instrument rating,
 * which the legacy form handled through a separate control. Privileges
 * (grade-3-and-up progression) are out of this MVP: they need the
 * add-rating-per-grade flow, and no certificate can be created without them
 * today either.
 */
const BLANK = {
	id: null,
	type: '',
	grade: '',
	number: '',
	fileNumber: '',
	frn: '',
	limitations: '',
	dateOfIssue: null,
	dateExpires: null,
	specialIssuance: false,
	ratings: []
};

// Medical expiries run down the classes: a First-class medical lapses to
// Second, then Third (legacy `prepareExpiresSet`).
const MEDICAL_CLASSES = ['First', 'Second', 'Third'];

/**
 * CertificatePreview — the record as stored: only the fields its type has
 * (`certificateFields`), the ratings, the expiry per medical class, and the scans.
 */
function CertificatePreview({ cert, typeName, gradeName, attachments }) {
	const f = fieldsForType(cert.type);
	const grade = gradeName(cert.grade);
	const exp = expiryState(cert.dateExpires);
	const ratingNames = (Array.isArray(cert.ratings) ? cert.ratings : []).map(r => r.name).filter(Boolean);

	const start = MEDICAL_CLASSES.indexOf(grade);
	const medicalRows =
		f.dateExpiresMedical && start >= 0
			? [cert.dateExpires, cert.secondDateExpires, cert.thirdDateExpires]
					.map((d, i) => [MEDICAL_CLASSES[start + i], d])
					.filter(([cls, d]) => cls && d)
			: [];

	return (
		<div className="flex flex-col gap-5">
			<dl className="flex flex-col">
				<PreviewRow label="Certificate Type">{typeName(cert.type) || '—'}</PreviewRow>
				{f.grade ? <PreviewRow label={labelFor(cert.type, 'grade', 'Grade')}>{grade || '—'}</PreviewRow> : null}
				{f.certificateNumber ? (
					<PreviewRow label={labelFor(cert.type, 'certificateNumber', 'Certificate Number')}>
						{cert.number || '—'}
					</PreviewRow>
				) : null}
				{f.fileNumber ? <PreviewRow label="File Number">{cert.fileNumber || '—'}</PreviewRow> : null}
				{f.frn ? <PreviewRow label="FRN">{cert.frn || '—'}</PreviewRow> : null}
				{f.dateOfIssue ? (
					<PreviewRow label={labelFor(cert.type, 'dateOfIssue', 'Date Of Issue')}>
						{formatUserDate(cert.dateOfIssue, '—')}
					</PreviewRow>
				) : null}
				{medicalRows.length ? (
					medicalRows.map(([cls, d], i) => (
						<PreviewRow key={cls} label={`${cls}-class privileges until`}>
							<span className="inline-flex flex-wrap items-center gap-2">
								{formatUserDate(d)}
								{i === 0 && exp ? (
									<Chip size="sm" variant="flat" color={exp.tone}>
										{exp.label}
									</Chip>
								) : null}
							</span>
						</PreviewRow>
					))
				) : f.dateExpires || f.dateExpiresMedical ? (
					<PreviewRow label="Expiration Date">
						{cert.dateExpires ? (
							<span className="inline-flex flex-wrap items-center gap-2">
								{formatUserDate(cert.dateExpires)}
								{exp ? (
									<Chip size="sm" variant="flat" color={exp.tone}>
										{exp.label}
									</Chip>
								) : null}
							</span>
						) : (
							<span className="text-default-400">No expiration</span>
						)}
					</PreviewRow>
				) : null}
				{f.specialIssuance && cert.specialIssuance ? <PreviewRow label="Special Issuance">Yes</PreviewRow> : null}
				{f.limitations ? (
					<PreviewRow label={labelFor(cert.type, 'limitations', 'Limitations')}>
						{cert.limitations || '—'}
					</PreviewRow>
				) : null}
				{ratingNames.length ? (
					<PreviewRow label="Ratings">
						<span className="flex flex-wrap gap-1.5">
							{ratingNames.map(n => (
								<Chip key={n} size="sm" variant="flat" className="bg-default-100 text-default-700">
									{n}
								</Chip>
							))}
						</span>
					</PreviewRow>
				) : null}
			</dl>

			<div className="flex flex-col gap-2">
				<span className="text-[13px] font-semibold text-foreground">Scans</span>
				<AttachmentList items={attachments} />
			</div>
		</div>
	);
}

function CertificateDialog({
	isOpen,
	onClose,
	certificate,
	types,
	grades,
	ratings,
	onSave,
	saving,
	onUploadAsset,
	onDeleteAsset,
	uploading,
	mode = 'edit',
	onModeChange,
	onDelete,
	deleting = false
}) {
	const [form, setForm] = useState(BLANK);
	const [ratingToAdd, setRatingToAdd] = useState(null);
	const attachments = useMemo(() => toAttachmentList(certificate), [certificate]);

	useEffect(() => {
		if (!isOpen) return;
		setRatingToAdd(null);
		if (!certificate) {
			setForm(BLANK);
			return;
		}
		setForm({
			id: certificate.id ?? null,
			type: certificate.type == null ? '' : String(certificate.type),
			grade: certificate.grade == null ? '' : String(certificate.grade),
			number: certificate.number || '',
			fileNumber: certificate.fileNumber || '',
			frn: certificate.frn || '',
			limitations: certificate.limitations || '',
			dateOfIssue: toPickerValue(certificate.dateOfIssue),
			dateExpires: toPickerValue(certificate.dateExpires),
			specialIssuance: Boolean(certificate.specialIssuance),
			ratings: Array.isArray(certificate.ratings)
				? certificate.ratings.map(r => String(r.id != null ? r.id : r))
				: []
		});
		// Re-seeded on open, on another record and on every mode switch (so Cancel
		// drops unsaved edits) — NOT when the same record is re-read after a scan
		// upload, which would wipe what is being typed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, certificate && certificate.id, mode]);

	const fields = useMemo(() => fieldsForType(form.type), [form.type]);
	const typeGrades = useMemo(() => gradesForType(grades, form.type), [grades, form.type]);
	const typeRatings = useMemo(
		() => ratingsForType(ratings, form.type, fields.instrumentalID),
		[ratings, form.type, fields.instrumentalID]
	);

	const set = (k, v) => setForm(prev => (prev[k] === v ? prev : { ...prev, [k]: v }));
	const typeName = id => ((types || []).find(t => String(t.id) === String(id)) || {}).name || '';
	const gradeName = id => ((grades || []).find(g => String(g.id) === String(id)) || {}).name || '';

	const chosenRatings = useMemo(
		() => form.ratings.map(id => typeRatings.find(r => r.id === id)).filter(Boolean),
		[form.ratings, typeRatings]
	);
	const addableRatings = useMemo(
		() => typeRatings.filter(r => !form.ratings.includes(r.id)),
		[typeRatings, form.ratings]
	);

	const showRatings = Boolean(
		fields.ratingsWithPrivilege || fields.ratingsWithoutPrivilege || fields.ratingsWithoutType
	);
	const canSave = Boolean(form.type);

	const control = {
		variant: 'bordered',
		labelPlacement: 'outside',
		radius: 'sm',
		classNames: { inputWrapper: 'h-10 min-h-10', trigger: 'h-10 min-h-10' }
	};

	// Changing the type invalidates grade and ratings — they are type-scoped.
	const handleTypeChange = key => {
		const next = key == null ? '' : String(key);
		setForm(prev => ({ ...prev, type: next, grade: '', ratings: [] }));
	};

	const viewing = mode === 'view' && certificate;

	return (
		<CredentialDrawer
			isOpen={isOpen}
			onClose={onClose}
			icon={Award}
			title={viewing ? typeName(certificate.type) || 'Certificate' : form.id ? 'Edit Certificate' : 'Add Certificate'}
			subtitle={
				viewing
					? [gradeName(certificate.grade), certificate.number].filter(Boolean).join(' · ')
					: 'Fields below follow the selected certificate type.'
			}
			mode={viewing ? 'view' : 'edit'}
			onEdit={onModeChange ? () => onModeChange('edit') : undefined}
			onDelete={onDelete ? () => onDelete(certificate) : undefined}
			deleting={deleting}
			footer={
				viewing ? null : (
					<>
						<Button
							variant="bordered"
							radius="sm"
							onPress={() => (form.id && onModeChange ? onModeChange('view') : onClose())}
						>
							Cancel
						</Button>
						<Button
							color="primary"
							radius="sm"
							isDisabled={!canSave}
							isLoading={saving}
							onPress={() => onSave(form, fields)}
						>
							{form.id ? 'Save Certificate' : 'Add Certificate'}
						</Button>
					</>
				)
			}
		>
			{viewing ? (
				<CertificatePreview
					cert={certificate}
					typeName={typeName}
					gradeName={gradeName}
					attachments={attachments}
				/>
			) : (
				<>
					<Autocomplete
						{...control}
						label="Certificate Type"
						isRequired
						defaultItems={types}
						selectedKey={form.type ? String(form.type) : null}
						onSelectionChange={handleTypeChange}
						placeholder="Search certificate types"
						menuTrigger="input"
					>
						{t => <AutocompleteItem key={String(t.id)}>{t.name}</AutocompleteItem>}
					</Autocomplete>

					{fields.grade && typeGrades.length > 0 && (
						<Autocomplete
							{...control}
							label={labelFor(form.type, 'grade', 'Grade')}
							defaultItems={typeGrades}
							selectedKey={form.grade ? String(form.grade) : null}
							onSelectionChange={key => set('grade', key == null ? '' : String(key))}
							placeholder="Search"
							menuTrigger="input"
						>
							{g => <AutocompleteItem key={String(g.id)}>{g.name}</AutocompleteItem>}
						</Autocomplete>
					)}

					{fields.certificateNumber && (
						<Input
							{...control}
							label={labelFor(form.type, 'certificateNumber', 'Certificate Number')}
							value={form.number}
							onValueChange={v => set('number', v)}
						/>
					)}

					{(fields.fileNumber || fields.frn) && (
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							{fields.fileNumber && (
								<Input
									{...control}
									label={labelFor(form.type, 'fileNumber', 'File Number')}
									value={form.fileNumber}
									onValueChange={v => set('fileNumber', v)}
								/>
							)}
							{fields.frn && (
								<Input
									{...control}
									label={labelFor(form.type, 'frn', 'FRN')}
									value={form.frn}
									onValueChange={v => set('frn', v)}
								/>
							)}
						</div>
					)}

					{(fields.dateOfIssue || fields.dateExpires || fields.dateExpiresMedical) && (
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							{fields.dateOfIssue && (
								<AppDatePicker
									label={labelFor(form.type, 'dateOfIssue', 'Date Of Issue')}
									variant="bordered"
									labelPlacement="outside"
									radius="sm"
									granularity="day"
									value={form.dateOfIssue}
									onChange={v => set('dateOfIssue', v)}
								/>
							)}
							{/* A medical expiry is computed from class and age; only a
							    special issuance takes the date typed here. */}
							{(fields.dateExpiresMedical ? form.specialIssuance : fields.dateExpires) && (
								<AppDatePicker
									label={labelFor(form.type, 'dateExpires', 'Expires')}
									variant="bordered"
									labelPlacement="outside"
									radius="sm"
									granularity="day"
									value={form.dateExpires}
									onChange={v => set('dateExpires', v)}
								/>
							)}
						</div>
					)}

					{fields.specialIssuance && (
						<div className="flex flex-col gap-1">
							<Checkbox
								size="sm"
								isSelected={form.specialIssuance}
								onValueChange={v => set('specialIssuance', v)}
							>
								<span className="text-[13px]">Special issuance</span>
							</Checkbox>
							<p className="text-[12px] text-default-400 m-0">
								{form.specialIssuance
									? 'The expiry date above is used as typed.'
									: 'Expiry dates are calculated from the class and your age at the date of issue.'}
							</p>
						</div>
					)}

					{showRatings && (
						<div className="flex flex-col gap-2">
							<span className="text-[13px] font-medium text-default-700">Ratings</span>

							{chosenRatings.length > 0 ? (
								<div className="flex flex-wrap gap-2">
									{chosenRatings.map(r => (
										<Chip
											key={r.id}
											variant="flat"
											color="primary"
											endContent={
												<button
													type="button"
													aria-label={`Remove ${r.name}`}
													className="border-0 bg-transparent cursor-pointer p-0 flex items-center"
													onClick={() =>
														set(
															'ratings',
															form.ratings.filter(id => id !== r.id)
														)
													}
												>
													<X className="w-3.5 h-3.5" />
												</button>
											}
										>
											{r.name}
										</Chip>
									))}
								</div>
							) : (
								<p className="text-[12.5px] text-default-400 m-0">No ratings added yet.</p>
							)}

							<div className="flex items-end gap-2">
								<Autocomplete
									{...control}
									aria-label="Select a rating to add"
									className="flex-1"
									defaultItems={addableRatings}
									selectedKey={ratingToAdd}
									onSelectionChange={key => setRatingToAdd(key == null ? null : String(key))}
									placeholder="Select a rating to add"
									menuTrigger="input"
									isDisabled={!form.type || addableRatings.length === 0}
								>
									{r => <AutocompleteItem key={String(r.id)}>{r.name}</AutocompleteItem>}
								</Autocomplete>
								<Button
									variant="bordered"
									radius="sm"
									isDisabled={!ratingToAdd}
									onPress={() => {
										if (!ratingToAdd) return;
										set('ratings', [...form.ratings, ratingToAdd]);
										setRatingToAdd(null);
									}}
								>
									Add
								</Button>
							</div>
						</div>
					)}

					{fields.limitations && (
						<Input
							{...control}
							label={labelFor(form.type, 'limitations', 'Limitations')}
							value={form.limitations}
							onValueChange={v => set('limitations', v)}
						/>
					)}

					{/* Scans hang off the certificate id: offered once it exists. */}
					{certificate?.id ? (
						<div className="flex flex-col gap-2 pt-1">
							<span className="text-[13px] font-medium text-default-700">Scans</span>
							<AttachmentList
								items={attachments}
								multiple
								onUpload={files => onUploadAsset(certificate.id, files)}
								onDelete={item => onDeleteAsset(certificate.id, item)}
								uploading={uploading}
							/>
						</div>
					) : (
						<p className="text-[12.5px] text-default-500 m-0">
							Save the certificate first — scans can be attached to it afterwards.
						</p>
					)}
				</>
			)}
		</CredentialDrawer>
	);
}

export default CertificateDialog;
